// ─── Siege System Types & Mock Data ─────────────────────────────────────────

export type AccountState = 'ACTIVE' | 'RESTING' | 'BLOWN' | 'REPAIR';

export interface SiegeAccount {
  id: string;
  firmId: string;
  firmAbbr: string;
  size: number;
  state: AccountState;
  cohortId: string;
  isMaster: boolean;
  dateFunded: string;
  dateBlown?: string;
  payoutCount: number;
}

export interface Cohort {
  id: string;
  label: string;           // "A", "B", "C", "D"
  clusterId?: number;       // Phase 5 only: 1 or 2
  accountIds: string[];
  isActive: boolean;
}

export interface SiegePortfolio {
  currentPhase: 1 | 2 | 3 | 4 | 5 | 6;
  accounts: SiegeAccount[];
  cohorts: Cohort[];
  activeCohortId: string;
  rotationSequence: string[];
  tradeInCycle: number;     // which trade in the current full rotation
  gate1Payout: boolean;
  gate2DaysClean: number;
  gate2LastBreach: string | null;
  gate3TradeCount: number;
  gate3Target: number;
  strategy: 'TCL' | 'SMOG';
}

// ─── Phase definitions ──────────────────────────────────────────────────────

export const PHASE_DEFS = [
  { phase: 1, accounts: 3,  tier: 'Shield'  as const, cohorts: 2, subsPerCohort: 1, hasMaster: true },
  { phase: 2, accounts: 5,  tier: 'Shield'  as const, cohorts: 2, subsPerCohort: 2, hasMaster: true },
  { phase: 3, accounts: 7,  tier: 'Shield'  as const, cohorts: 2, subsPerCohort: 3, hasMaster: true },
  { phase: 4, accounts: 10, tier: 'Sword'   as const, cohorts: 3, subsPerCohort: 3, hasMaster: true },
  { phase: 5, accounts: 14, tier: 'Sword'   as const, cohorts: 6, subsPerCohort: 2, hasMaster: true, clusters: 2 },
  { phase: 6, accounts: 20, tier: 'Hammer'  as const, cohorts: 4, subsPerCohort: 5, hasMaster: false },
];

export function getPhaseTierColor(phase: number): string {
  if (phase <= 3) return '#3DBF7A';
  if (phase <= 5) return '#E8943A';
  return '#E84040';
}

export const COHORT_COLORS: Record<string, string> = {
  A: '#60A5FA',
  B: '#A78BFA',
  C: '#34D399',
  D: '#FB923C',
  A1: '#60A5FA',
  B1: '#A78BFA',
  C1: '#34D399',
  A2: '#818CF8',
  B2: '#F472B6',
  C2: '#FBBF24',
};

// ─── Mock portfolio (Phase 2 — 5 accounts) ──────────────────────────────────

export const MOCK_PORTFOLIO_P2: SiegePortfolio = {
  currentPhase: 2,
  strategy: 'TCL',
  activeCohortId: 'cohort-a',
  rotationSequence: ['cohort-a', 'cohort-b'],
  tradeInCycle: 1,
  gate1Payout: true,
  gate2DaysClean: 18,
  gate2LastBreach: '2026-04-02',
  gate3TradeCount: 47,
  gate3Target: 50,
  accounts: [
    { id: 'acc-m', firmId: 'apex', firmAbbr: 'APX', size: 50000, state: 'ACTIVE', cohortId: 'master', isMaster: true, dateFunded: '2026-01-15', payoutCount: 2 },
    { id: 'acc-1', firmId: 'topstep', firmAbbr: 'TOP', size: 50000, state: 'ACTIVE', cohortId: 'cohort-a', isMaster: false, dateFunded: '2026-01-20', payoutCount: 1 },
    { id: 'acc-2', firmId: 'lucid', firmAbbr: 'LUC', size: 50000, state: 'ACTIVE', cohortId: 'cohort-a', isMaster: false, dateFunded: '2026-02-01', payoutCount: 0 },
    { id: 'acc-3', firmId: 'tradeify', firmAbbr: 'TRD', size: 50000, state: 'RESTING', cohortId: 'cohort-b', isMaster: false, dateFunded: '2026-02-10', payoutCount: 1 },
    { id: 'acc-4', firmId: 'mff', firmAbbr: 'MFF', size: 50000, state: 'RESTING', cohortId: 'cohort-b', isMaster: false, dateFunded: '2026-02-15', payoutCount: 0 },
  ],
  cohorts: [
    { id: 'cohort-a', label: 'A', accountIds: ['acc-1', 'acc-2'], isActive: true },
    { id: 'cohort-b', label: 'B', accountIds: ['acc-3', 'acc-4'], isActive: false },
  ],
};

// ─── Mock portfolio (Phase 5 — 14 accounts, dual clusters) ─────────────────

export const MOCK_PORTFOLIO: SiegePortfolio = {
  currentPhase: 5,
  strategy: 'TCL',
  activeCohortId: 'c1-a',
  rotationSequence: ['c1-a', 'c1-b', 'c1-c', 'c2-a', 'c2-b', 'c2-c'],
  tradeInCycle: 2,
  gate1Payout: true,
  gate2DaysClean: 24,
  gate2LastBreach: '2026-03-22',
  gate3TradeCount: 38,
  gate3Target: 50,
  accounts: [
    // Cluster 1 — Master
    { id: 'm1', firmId: 'apex', firmAbbr: 'APX', size: 50000, state: 'ACTIVE', cohortId: 'master-1', isMaster: true, dateFunded: '2025-11-01', payoutCount: 5 },
    // Cluster 1 — Cohort A1
    { id: 'c1a-1', firmId: 'topstep', firmAbbr: 'TOP', size: 50000, state: 'ACTIVE', cohortId: 'c1-a', isMaster: false, dateFunded: '2025-11-15', payoutCount: 3 },
    { id: 'c1a-2', firmId: 'lucid', firmAbbr: 'LUC', size: 50000, state: 'ACTIVE', cohortId: 'c1-a', isMaster: false, dateFunded: '2025-12-01', payoutCount: 2 },
    // Cluster 1 — Cohort B1
    { id: 'c1b-1', firmId: 'tradeify', firmAbbr: 'TRD', size: 50000, state: 'RESTING', cohortId: 'c1-b', isMaster: false, dateFunded: '2025-12-10', payoutCount: 2 },
    { id: 'c1b-2', firmId: 'mff', firmAbbr: 'MFF', size: 50000, state: 'RESTING', cohortId: 'c1-b', isMaster: false, dateFunded: '2025-12-20', payoutCount: 1 },
    // Cluster 1 — Cohort C1
    { id: 'c1c-1', firmId: 'apex', firmAbbr: 'APX', size: 100000, state: 'RESTING', cohortId: 'c1-c', isMaster: false, dateFunded: '2026-01-05', payoutCount: 1 },
    { id: 'c1c-2', firmId: 'topstep', firmAbbr: 'TOP', size: 100000, state: 'BLOWN', cohortId: 'c1-c', isMaster: false, dateFunded: '2026-01-10', dateBlown: '2026-04-08', payoutCount: 0 },
    // Cluster 2 — Master
    { id: 'm2', firmId: 'lucid', firmAbbr: 'LUC', size: 50000, state: 'ACTIVE', cohortId: 'master-2', isMaster: true, dateFunded: '2026-01-15', payoutCount: 2 },
    // Cluster 2 — Cohort A2
    { id: 'c2a-1', firmId: 'tradeify', firmAbbr: 'TRD', size: 50000, state: 'RESTING', cohortId: 'c2-a', isMaster: false, dateFunded: '2026-01-20', payoutCount: 1 },
    { id: 'c2a-2', firmId: 'mff', firmAbbr: 'MFF', size: 50000, state: 'RESTING', cohortId: 'c2-a', isMaster: false, dateFunded: '2026-02-01', payoutCount: 1 },
    // Cluster 2 — Cohort B2
    { id: 'c2b-1', firmId: 'apex', firmAbbr: 'APX', size: 100000, state: 'RESTING', cohortId: 'c2-b', isMaster: false, dateFunded: '2026-02-10', payoutCount: 0 },
    { id: 'c2b-2', firmId: 'topstep', firmAbbr: 'TOP', size: 100000, state: 'REPAIR', cohortId: 'c2-b', isMaster: false, dateFunded: '2026-02-15', payoutCount: 0 },
    // Cluster 2 — Cohort C2
    { id: 'c2c-1', firmId: 'lucid', firmAbbr: 'LUC', size: 100000, state: 'RESTING', cohortId: 'c2-c', isMaster: false, dateFunded: '2026-03-01', payoutCount: 0 },
    { id: 'c2c-2', firmId: 'tradeify', firmAbbr: 'TRD', size: 100000, state: 'RESTING', cohortId: 'c2-c', isMaster: false, dateFunded: '2026-03-05', payoutCount: 0 },
  ],
  cohorts: [
    // Cluster 1
    { id: 'c1-a', label: 'A1', clusterId: 1, accountIds: ['c1a-1', 'c1a-2'], isActive: true },
    { id: 'c1-b', label: 'B1', clusterId: 1, accountIds: ['c1b-1', 'c1b-2'], isActive: false },
    { id: 'c1-c', label: 'C1', clusterId: 1, accountIds: ['c1c-1', 'c1c-2'], isActive: false },
    // Cluster 2
    { id: 'c2-a', label: 'A2', clusterId: 2, accountIds: ['c2a-1', 'c2a-2'], isActive: false },
    { id: 'c2-b', label: 'B2', clusterId: 2, accountIds: ['c2b-1', 'c2b-2'], isActive: false },
    { id: 'c2-c', label: 'C2', clusterId: 2, accountIds: ['c2c-1', 'c2c-2'], isActive: false },
  ],
};
